import { Icon, IconButton, Modal } from "@material-ui/core";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  ICreateDefferedPromise,
  createDefferedPromise,
} from "utils/DefferedPromise";
import styles from "./BottomModal.module.scss";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";

export interface IBottomModalHandler {
  open(): Promise<any>;
}

interface BottomModalProps {
  title?: string;
  image?: any;
  message?: string;
  confirmTitle?: string;
  onClickConfirm?: () => void;
  onClickClose?: () => void;
  hideCloseButton?: boolean;
  closeButtonTile?: string;
  children?: ReactNode;
  SvgIcon?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string;
    }
  >;
  disable?: boolean;
  autoCloseInMs?: number; 
}

const PromiseBottomModal = React.forwardRef(function BottomModal(
  {
    confirmTitle,
    image,
    message,
    onClickConfirm,
    onClickClose,
    title,
    hideCloseButton,
    closeButtonTile,
    children,
    SvgIcon,
    disable,
    autoCloseInMs,
  }: BottomModalProps,
  ref: React.Ref<IBottomModalHandler>
) {
  const deffered = useRef<ICreateDefferedPromise<any>>(createDefferedPromise());
  const { resolution } = useScreenStore();
  const [visible, setVisible] = useState(false);

  const open = useCallback(() => {
    deffered.current = createDefferedPromise<any>();
    setVisible(true);
    return deffered.current.promise;
  }, []);

  useImperativeHandle(ref, () => ({ open }));

  const confirm = useCallback(() => {
    onClickConfirm && onClickConfirm();
    setVisible(false);
    deffered.current.resolver({});
  }, [onClickConfirm]);

  const close = useCallback(() => {
    onClickClose && onClickClose();
    setAnimate(false);
    setTimeout(() => {
      setVisible(false);
      deffered.current.rejecter(
        new Error("Fechado pelo usuário ou tempo esgotado")
      );
    }, 300);
  }, [onClickClose]);

  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (visible) {
      setTimeout(() => setAnimate(true), 50);
    } else {
      setAnimate(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible || !autoCloseInMs) return;

    const timer = setTimeout(() => {
      close();
    }, autoCloseInMs);

    return () => clearTimeout(timer);
  }, [visible, autoCloseInMs, close]);

  return (
    <Modal
      open={visible}
      onClose={!hideCloseButton ? close : () => {}}
      BackdropProps={{
        style: {
          backdropFilter: "blur(10px)",
        },
      }}
    >
      <div
        className={styles.actionModalContainer}
        style={{
          transform: animate ? "translate(-50%, 0)" : "translate(-50%, 100%)",
          transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
          opacity: animate ? 1 : 0,
        }}
      >
        {!hideCloseButton && (
          <div className={styles.close}>
            <IconButton onClick={close}>
              <Icon>close</Icon>
            </IconButton>
          </div>
        )}
        <div className={styles.content}>
          {title && <div className={styles.title}>{title}</div>}
          {image && (
            <div className={styles.imageContainer}>
              <img className="image" src={image} alt="Imagem" />
            </div>
          )}
          {SvgIcon && <SvgIcon color="var(--primary)" />}
          <div className={styles.text}>{message}</div>
          <div>{children}</div>
          <div className={styles.buttonsContainer}>
            {confirmTitle && (
              <button
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 40 : 28,
                  height: resolution === ResolutionType.FULL_HD ? 120 : 80,
                  opacity: disable ? 0.5 : 1,
                }}
                disabled={disable}
                onClick={confirm}
              >
                {confirmTitle}
              </button>
            )}
            {closeButtonTile && (
              <button
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 40 : 28,
                  height: resolution === ResolutionType.FULL_HD ? 120 : 80,
                  opacity: disable ? 0.5 : 1,
                }}
                disabled={disable}
                onClick={close}
              >
                {closeButtonTile}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
});
export default PromiseBottomModal;
